import { cleanUrl, displayHost } from '../utils/url';

/** The article an image appeared in (`metadata.article`). */
export class ImageArticle {
  url!: string;
  title: string | null = null;
  domainName: string | null = null;
  publicationDate: string | null = null;

  constructor(data: any = {}) {
    this.url = cleanUrl(data.url) ?? '';
    this.title = typeof data.title === 'string' ? data.title.replace(/\s+/g, ' ').trim() || null : null;
    this.domainName = data.domain_name || null;
    this.publicationDate = data.publication_date ?? null;
  }

  /** The domain to show, without "www.". */
  get host(): string {
    return displayHost(this.domainName, this.url);
  }
}

/** What the image analysis found in it (`metadata.detected`), as counts. */
export interface ImageDetected {
  persons: number;
  faces: number;
  text: number;
  animals: number;
  sports: number;
  vehicles: number;
}

/** `metadata` of an /image result (include_metadata). Not in the API docs
 *  yet; read from real dev responses (2026-10-18). The caption fields
 *  (title, description, artist…) come only with agency photos. */
export class ImageMetadata {
  originalUrl: string | null = null;
  article: ImageArticle | null = null;
  width: number | null = null;
  height: number | null = null;
  detected: ImageDetected | null = null;
  /** Dominant colours, largest share first; ratio in percent. */
  colors: { name: string; ratio: number }[] = [];
  /** Text read off the image. */
  ocr: string | null = null;
  title: string | null = null;
  description: string | null = null;
  artist: string | null = null;
  copyright: string | null = null;
  source: string | null = null;
  city: string | null = null;
  province: string | null = null;
  country: string | null = null;

  constructor(data: any = {}) {
    this.originalUrl = cleanUrl(data.original_url);
    this.article = data.article ? new ImageArticle(data.article) : null;
    this.width = typeof data.dimensions?.width === 'number' ? data.dimensions.width : null;
    this.height = typeof data.dimensions?.height === 'number' ? data.dimensions.height : null;
    this.detected = data.detected && typeof data.detected === 'object'
      ? {
          persons: data.detected.persons ?? 0,
          faces: data.detected.faces ?? 0,
          text: data.detected.text ?? 0,
          animals: data.detected.animals ?? 0,
          sports: data.detected.sports ?? 0,
          vehicles: data.detected.vehicles ?? 0,
        }
      : null;
    this.colors = (data.colors ?? [])
      .filter((c: any) => typeof c?.name === 'string' && typeof c?.ratio === 'number')
      .map((c: any) => ({ name: c.name, ratio: c.ratio }));
    for (const key of ['ocr', 'title', 'description', 'artist', 'copyright', 'source', 'city', 'province', 'country'] as const) {
      const value = data[key];
      this[key] = typeof value === 'string' && value.trim() ? value.trim() : null;
    }
  }

  /** "Washington, DC, USA", from whichever parts came back. */
  get place(): string | null {
    const parts = [this.city, this.province, this.country].filter(Boolean);
    return parts.length ? parts.join(', ') : null;
  }
}

/** One /image result. `url` (the image, as Cikisi stores it) is always
 *  there; `indexation_date` and `metadata` only with their include_* flag. */
export class ImageHit {
  url!: string;
  indexationDate: string | null = null;
  metadata: ImageMetadata | null = null;

  constructor(data: any = {}) {
    this.url = cleanUrl(data.url) ?? '';
    this.indexationDate = data.indexation_date ?? null;
    this.metadata = data.metadata && typeof data.metadata === 'object' ? new ImageMetadata(data.metadata) : null;
  }

  /** The site the image appeared on, for Top sites: the article's domain,
   *  else the image's own host. */
  get host(): string {
    return this.metadata?.article?.host ?? displayHost(null, this.url);
  }

  /** No favicon on /image; here so a hit fits Top sites. */
  readonly favicon = null;
}

export class ImageResultMeta {
  count!: number;
  totalMatches: number | null = null;
  sortBy: string | null = null;
  fields: string[] = [];
  parametersEcho: Record<string, unknown> = {};

  constructor(data: any = {}) {
    this.count = data.count;
    this.totalMatches = typeof data.total_matches === 'number' ? data.total_matches : null;
    this.sortBy = data.sort_by ?? null;
    this.fields = data.fields ?? [];
    this.parametersEcho = data.parameters_echo ?? {};
  }
}

/** The data object of an /image response: `query`, `results`, `meta`. */
export class ImageResult {
  query!: string;
  results: ImageHit[] = [];
  meta!: ImageResultMeta;

  constructor(data: any = {}) {
    this.query = data.query;
    this.results = (data.results ?? []).map((hit: object) => new ImageHit(hit));
    this.meta = new ImageResultMeta(data.meta ?? {});
    this.meta.count ??= this.results.length;
  }
}
